<!--
  CurrencyField.vue

  Cell renderer for `currency` fields. Inline shows the grouped amount;
  active/editing lift CurrencyInput (read-only until editing). Like ValueField,
  but currency-formatted, following the record's `data.currency`.
-->
<template>
  <div v-if="mode === 'inline'" class="currency-field" :class="sizeClass">
    <span v-if="isEmpty" class="cell-placeholder">{{ placeholderHint }}</span>
    <span v-else class="cell-value">{{ displayValue }}</span>
  </div>
  <CurrencyInput
    v-else
    ref="input"
    class="currency-field currency-field-editor"
    :model-value="numericValue"
    :field="field"
    :item="item"
    :size="size"
    :placeholder="placeholderHint"
    :readonly="mode !== 'editing'"
    @update:model-value="emit('update:modelValue', $event)"
    @changed="emit('commit')"
  />
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from "vue"
import type { AugerField } from "@auger/core/fields"
import { currencyFull } from "@auger/ui/utils/number"
import { type FieldMode, type FieldSize, textSizeClass } from "../useField"
import CurrencyInput from "./CurrencyInput.vue"

const props = withDefaults(
  defineProps<{
    modelValue: unknown
    field: AugerField
    mode: FieldMode
    item?: { data?: Record<string, unknown> | null } | null
    size?: FieldSize
    seeded?: boolean
  }>(),
  { item: null, size: "small", seeded: false }
)

const emit = defineEmits<{
  "update:modelValue": [value: unknown]
  commit: [payload?: { advance?: boolean }]
  cancel: []
}>()

const sizeClass = computed(() => textSizeClass(props.size))
const editable = computed(() => !!props.field.isEditable)
const currencyCode = computed<string>(() => (props.item?.data?.currency as string) || "USD")
const numericValue = computed<number | null>(() =>
  props.modelValue == null || props.modelValue === "" ? null : Number(props.modelValue)
)

const displayValue = computed(() => {
  const v = props.modelValue
  return v == null || v === "" ? "" : (currencyFull(Number(v), currencyCode.value) as string)
})
const isEmpty = computed(() => displayValue.value === "")

const placeholderHint = computed(() => {
  if (!editable.value) return ""
  const fieldPlaceholder = props.field.placeholder
  if (typeof fieldPlaceholder === "string" && fieldPlaceholder !== "") return fieldPlaceholder
  return "$0.00"
})

const input = ref<{ $el?: HTMLElement } | null>(null)

// On edit-open, focus the editor: `seeded` (type-to-edit) puts the caret at the
// end to append, else select all so the first keystroke replaces. Like ValueField.
watch(
  () => props.mode === "editing",
  (on) => {
    if (!on) return
    const typeToEdit = props.seeded
    nextTick(() => {
      const root = input.value?.$el ?? (input.value as unknown as HTMLElement | null)
      const el = (
        root?.matches?.("input") ? root : root?.querySelector?.("input")
      ) as HTMLInputElement | null
      if (!el) return
      el.focus()
      if (typeToEdit) {
        const end = el.value.length
        el.setSelectionRange(end, end)
      } else {
        el.select()
      }
    })
  }
)
</script>

<style>
.currency-field {
  display: flex;
  align-items: center;
  min-width: 0;
  height: 100%;
  overflow: hidden;
  cursor: default;
  font-variant-numeric: tabular-nums;

  /* Empty-cell hint: hidden until hover, then a faint prompt. */
  .cell-placeholder {
    color: var(--auger-text-weak);
    opacity: 0;
    transition: opacity 0.1s ease;
  }

  &:hover .cell-placeholder {
    opacity: 0.6;
  }
}
</style>
